import { useState } from 'react'
import { CirclePause, Play } from 'lucide-react'
import { toast } from 'sonner'
import type { components } from '@/api/schema'
import { useCurrentUser, useHoldPurchaseOrder, useReleasePurchaseOrderHold } from '@/api/hooks'
import { ActionPopover } from '@/components/action-popover'
import { Button } from '@/components/ui/button'
import { formatApiErrorMessage } from '@/lib/api-error'
import { ComposeTextarea } from '@/components/compose-field'

type PurchaseOrder = components['schemas']['PurchaseOrderDetailRead']

const HOLD_ROLES = new Set([
  'admin',
  'operations',
  'ops',
  'project_manager',
  'purchasing',
  'shipping',
  'warehousing',
])

function normalizeRole(role: string | null | undefined) {
  return (role ?? '').trim().toLowerCase().replaceAll('-', '_').replaceAll(' ', '_')
}

export function POOperationalHold({ po }: { po: PurchaseOrder }) {
  const [note, setNote] = useState('')
  const { data: currentUser } = useCurrentUser()
  const holdMutation = useHoldPurchaseOrder(po.uuid)
  const releaseMutation = useReleasePurchaseOrderHold(po.uuid)
  const isHeld = Boolean(po.operational_held_at)
  // What the popover was opened to do. It keeps that while it closes, even though the saved PO
  // has already flipped, so it never redraws as the opposite action on its way out.
  const [releasing, setReleasing] = useState(isHeld)
  const canManageHold = HOLD_ROLES.has(normalizeRole(currentUser?.role))
  const pending = holdMutation.isPending || releaseMutation.isPending

  if (!canManageHold) return null

  function submit(reason: string, close: () => void) {
    if (!releasing) {
      holdMutation.mutate(
        { reason, note: note.trim() || null },
        {
          onSuccess: () => {
            toast.success('Purchase order placed on hold')
            setNote('')
            close()
          },
          onError: (error) => toast.error(formatApiErrorMessage('Could not place hold', error)),
        },
      )
      return
    }
    releaseMutation.mutate(
      { reason },
      {
        onSuccess: () => {
          toast.success('Purchase order hold released')
          close()
        },
        onError: (error) => toast.error(formatApiErrorMessage('Could not release hold', error)),
      },
    )
  }

  return (
    // Sits beside the status pills in the PO header, as small as they are (USA-1432).
    <ActionPopover
      trigger={
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 px-2 text-xs"
          aria-label={isHeld ? 'Release hold' : 'Place hold'}
          title={isHeld ? 'Release hold' : 'Place hold'}
        >
          {isHeld ? (
            <Play className="size-3.5" aria-hidden="true" />
          ) : (
            <CirclePause className="size-3.5" aria-hidden="true" />
          )}
          {isHeld ? 'Release hold' : 'Hold'}
        </Button>
      }
      title={releasing ? 'Release operational hold' : 'Place operational hold'}
      note={{
        label: releasing ? 'Release reason' : 'Hold reason',
        placeholder: releasing ? 'Why release it?' : 'Why hold it?',
        minLength: 1,
        maxLength: 500,
        singleLine: true,
      }}
      confirmLabel={releasing ? 'Release hold' : 'Place hold'}
      pending={pending}
      pendingLabel={releasing ? 'Releasing...' : 'Placing hold...'}
      onOpenChange={(open) => {
        if (open) setReleasing(isHeld)
        else setNote('')
      }}
      focusNote
      onConfirm={submit}
    >
      {!releasing && (
        <ComposeTextarea
          aria-label="Note (optional)"
          placeholder="Note (optional)"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          maxLength={2000}
          disabled={pending}
          className="order-last min-h-16 text-sm"
        />
      )}
    </ActionPopover>
  )
}
